// İçerik koruması — sağ tık / seçim / kopyala / yapıştır / geliştirici kısayolu.
//
// Beş yüzeyin (platform, portal, cc portal, denetçi, vitrin) ORTAK uygulaması.
// Kararı sunucu veriyor: yüzey başına TEK istek, kural listesi çağırana göre
// zaten süzülmüş geliyor (muaf kullanıcıya boş liste). Burada yalnızca
// "hangi kural bu yola uyuyor" seçilip olay dinleyicileri takılıyor.
//
// ── DÜRÜST SINIR ───────────────────────────────────────────────────────────
// Bunlar KAZARA kopyalamayı keser, kararlı birini durdurmaz:
//   • F12 ve Ctrl+Shift+I tarayıcı düzeyinde yakalanır — `preventDefault()`
//     Chrome'da geliştirici araçlarını AÇMAYI ENGELLEMEZ. Menüden yine açılır.
//   • Ekran görüntüsü işletim sisteminde alınır; sayfanın haberi bile olmaz.
//   • JavaScript kapatılırsa hiçbiri çalışmaz.
// Engellenebilenler: sağ tık menüsü, metin seçimi, kopyala/kes/yapıştır,
// Ctrl+U (kaynağı görüntüle), Ctrl+S, Ctrl+P.
// Caydırıcılığı asıl taşıyan FİLİGRAN (kim, ne zaman) — ekran görüntüsünün
// üstünde de durur — ve sunucudaki erişim kaydıdır.

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { API_BASE } from '@/app/core/auth/auth.service';

export type ProtectionSurface = 'platform' | 'portal' | 'member' | 'inspector' | 'storefront';

export interface ProtectionFlags {
  pathPrefix: string | null;
  blockContextMenu: boolean;
  blockSelection: boolean;
  blockCopy: boolean;
  blockPaste: boolean;
  blockShortcuts: boolean;
  showWatermark: boolean;
}

interface ProtectionState {
  surface: string;
  source: string;
  watermarkText: string | null;
  rules: ProtectionFlags[];
}

const KAPALI: ProtectionFlags = {
  pathPrefix: null,
  blockContextMenu: false,
  blockSelection: false,
  blockCopy: false,
  blockPaste: false,
  blockShortcuts: false,
  showWatermark: false,
};

/** Engellenen tuş birleşimleri — hepsi `Ctrl`/`Cmd` ile (F12 hariç). */
const KISAYOLLAR = new Set(['u', 's', 'p']);
const KISAYOLLAR_SHIFT = new Set(['i', 'j', 'c']);

@Injectable({ providedIn: 'root' })
export class ContentProtection {
  private http = inject(HttpClient);
  private router = inject(Router);
  private doc = inject(DOCUMENT);
  private tarayici = isPlatformBrowser(inject(PLATFORM_ID));

  private state = signal<ProtectionState | null>(null);
  private yol = signal('/');
  private yuzey: ProtectionSurface | null = null;
  private kuruldu = false;

  /** Bu yol için geçerli bayraklar — EN UZUN eşleşen önek kazanır. */
  readonly flags = computed<ProtectionFlags>(() => {
    const s = this.state();
    if (!s || s.rules.length === 0) return KAPALI;

    const yol = this.yol();

    // Sunucu zaten önek uzunluğuna göre sıralı gönderiyor; yine de burada
    // sıralamaya güvenmiyoruz — listeyi elle süzüp en uzununu seçiyoruz ki
    // sunucu sıralaması bir gün değişirse koruma sessizce kaymasın.
    let kazanan: ProtectionFlags | null = null;
    let uzunluk = -1;

    for (const k of s.rules) {
      const onek = (k.pathPrefix ?? '').toLowerCase();
      if (onek && !yol.startsWith(onek)) continue;
      if (onek.length > uzunluk) {
        uzunluk = onek.length;
        kazanan = k;
      }
    }

    return kazanan ?? KAPALI;
  });

  readonly watermarkText = computed(() =>
    this.flags().showWatermark ? (this.state()?.watermarkText ?? null) : null,
  );

  /**
   * Seçim engelini `<html>` sınıfına yansıtır. Alan olarak kuruluyor çünkü
   * `effect` enjeksiyon bağlamı ister; `init()` bir bileşenden çağrılıyor ve
   * orada bağlam yok. Bayrak değişmedikçe DOM'a dokunmaz.
   */
  private readonly secimEtkisi = effect(() => {
    if (!this.tarayici) return;
    this.doc.documentElement.classList.toggle('mb-no-select', this.flags().blockSelection);
  });

  /**
   * Uygulama açılışında BİR KEZ çağrılır. `surface` sabitse (cc portal, denetçi
   * portalı) verilir; melland uygulamasında yol değiştikçe yüzey de değişebilir
   * (`/admin` ↔ `/portal` ↔ vitrin), o yüzden boş bırakılır ve yoldan çıkarılır.
   */
  init(surface?: ProtectionSurface): void {
    // SSR'de DOM yok ve ziyaretçinin kimliği de yok — koruma tarayıcıda kurulur.
    if (!this.tarayici || this.kuruldu) return;
    this.kuruldu = true;

    this.sabitYuzey = surface ?? null;

    this.yolDegisti(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.yolDegisti(e.urlAfterRedirects));

    this.dinleyicileriKur();
  }

  /**
   * Kimlik değiştiğinde (giriş/çıkış) yeniden sorar: muafiyet kişiye bağlı,
   * eski cevapla devam etmek yanlış kişiye yanlış korumayı uygular.
   */
  refresh(): void {
    if (!this.tarayici || !this.yuzey) return;
    this.cek(this.yuzey);
  }

  // ── İç işleyiş ───────────────────────────────────────────────────────────

  private sabitYuzey: ProtectionSurface | null = null;

  private yolDegisti(url: string): void {
    const temiz = this.temizle(url);
    const yeniYuzey = this.sabitYuzey ?? this.yuzeyiBul(temiz);

    this.yol.set(this.yolaCevir(temiz, yeniYuzey));

    if (yeniYuzey !== this.yuzey) {
      this.yuzey = yeniYuzey;
      this.state.set(null);
      this.cek(yeniYuzey);
    }
  }

  private cek(surface: ProtectionSurface): void {
    this.http
      .get<ProtectionState>(`${API_BASE}/content-protection/resolve`, { params: { surface } })
      .subscribe({
        next: (r) => this.state.set(r),
        // Ayar okunamadıysa ekranı kilitlemeyiz — koruma bir kolaylık,
        // erişim kapısı değil. Kapı sunucudaki yetkilendirmede.
        error: () => this.state.set(null),
      });
  }

  /** Sorgu dizgesi ve çapa atılır, küçük harfe indirilir. */
  private temizle(url: string): string {
    let t = url.split('?')[0].split('#')[0].trim().toLowerCase();
    if (!t.startsWith('/')) t = '/' + t;
    return t;
  }

  private yuzeyiBul(yol: string): ProtectionSurface {
    if (yol.startsWith('/admin')) return 'platform';
    if (yol.startsWith('/portal')) return 'portal';
    // Vitrin (melbarrel.com) ve kiracı vitrini aynı kefede: ikisinde de giriş
    // yapmış bir kullanıcı yok, tek tutamak IP.
    return 'storefront';
  }

  /**
   * Vitrinde yolun başındaki dil öneki atılır: `/en/hakkimizda` → `/hakkimizda`.
   * Atılmasaydı süper yöneticinin yazdığı `/fiyatlar` kuralı yalnız dilsiz
   * adreste tutar, `/en/fiyatlar`'da sessizce kapalı kalırdı.
   */
  private yolaCevir(yol: string, surface: ProtectionSurface): string {
    if (surface !== 'storefront') return yol;

    const parcalar = yol.split('/');
    if (parcalar.length > 1 && /^[a-z]{2}(-[a-z]{2})?$/.test(parcalar[1])) {
      const kalan = '/' + parcalar.slice(2).join('/');
      return kalan === '/' ? '/' : kalan.replace(/\/+$/, '') || '/';
    }

    return yol;
  }

  // ── DOM ──────────────────────────────────────────────────────────────────

  private dinleyicileriKur(): void {
    const d = this.doc;

    d.addEventListener('contextmenu', (e) => {
      if (this.flags().blockContextMenu && !this.yazilabilir(e.target)) e.preventDefault();
    });

    d.addEventListener('copy', (e) => {
      if (this.flags().blockCopy && !this.yazilabilir(e.target)) e.preventDefault();
    });

    d.addEventListener('cut', (e) => {
      if (this.flags().blockCopy && !this.yazilabilir(e.target)) e.preventDefault();
    });

    d.addEventListener('paste', (e) => {
      if (this.flags().blockPaste) e.preventDefault();
    });

    d.addEventListener('keydown', (e) => this.tusaBasildi(e));

    // Seçim engeli CSS ile: `selectstart` dinlemek de olurdu ama o, seçimi
    // engellerken imleci de bozuyor (kullanıcı sürükleyip hiçbir şey olmadığını
    // görüyor). `user-select: none` ile imleç zaten metin imleci olmuyor.
    this.stilKur();
  }

  private tusaBasildi(e: KeyboardEvent): void {
    if (!this.flags().blockShortcuts) return;

    // F12 ve Ctrl+Shift+I burada da yakalanıyor ama Chrome bunları tarayıcı
    // düzeyinde işliyor — `preventDefault()` geliştirici araçlarını AÇMAYI
    // engellemez. Yine de duruyor: Firefox'ta ve bazı gömülü tarayıcılarda
    // etkili, ve niyeti kayda geçiriyor.
    if (e.key === 'F12') {
      e.preventDefault();
      return;
    }

    const kontrol = e.ctrlKey || e.metaKey;
    if (!kontrol) return;

    const tus = e.key.toLowerCase();

    if (e.shiftKey && KISAYOLLAR_SHIFT.has(tus)) {
      e.preventDefault();
      return;
    }

    // Ctrl+C burada YOK: kopyalama `copy` olayında kesiliyor. Tuşu engellemek,
    // yazılabilir alanlardaki kopyalamayı da bozardı ve Ctrl+C'nin iptal
    // görevini (terminal benzeri alanlar) düşürürdü.
    if (!e.shiftKey && KISAYOLLAR.has(tus) && !this.yazilabilir(e.target)) {
      e.preventDefault();
    }
  }

  /**
   * Form alanları HER ZAMAN muaf. Olmasaydı korunan ekrandaki arama kutusuna
   * yazılanı düzeltmek bile imkânsızlaşırdı — koruma, kullanamama hâline gelir.
   */
  private yazilabilir(hedef: EventTarget | null): boolean {
    const el = hedef as HTMLElement | null;
    if (!el || !el.tagName) return false;

    const etiket = el.tagName.toLowerCase();
    return etiket === 'input' || etiket === 'textarea' || el.isContentEditable === true;
  }

  /**
   * Seçim engelini ve filigranı tek bir `<style>` ile kurar. Global stil
   * dosyasına yazmak yerine buradan: aynı servis üç ayrı uygulamada çalışıyor,
   * üç stil dosyasını eşlemek kaymaya açık olurdu.
   */
  private stilKur(): void {
    const stil = this.doc.createElement('style');
    stil.id = 'mb-content-protection';
    stil.textContent = [
      'html.mb-no-select, html.mb-no-select * {',
      '  -webkit-user-select: none; user-select: none;',
      '}',
      'html.mb-no-select input, html.mb-no-select textarea,',
      'html.mb-no-select [contenteditable="true"] {',
      '  -webkit-user-select: text; user-select: text;',
      '}',
    ].join('\n');
    this.doc.head.appendChild(stil);
    // Sınıfı `secimEtkisi` yönetiyor — sinyal değişince kendiliğinden.
  }
}
